import {
  getAllFoodSubcategoryKeywords,
  getFoodSubcategoryPriority,
  getLocaleAwareSubcategoryKeywords,
} from './languages';
import {
  type SubcategoryConfig,
  type SubcategoryResult,
  detectSubcategory,
} from './subcategoryDetection';
import { EXPENSE_TAXONOMY } from './taxonomy';

export const FOOD_KEYWORDS = getAllFoodSubcategoryKeywords();

/**
 * Subcategory keys the food detector can return. Derived from the
 * canonical `EXPENSE_TAXONOMY` so adding a key in one place
 * automatically flows through to the detector. Icon mapping lives in
 * `@splitfiesta/shared`'s `expense-subcategory-icons.ts`.
 */
export const VALID_FOOD_SUBCATEGORIES =
  EXPENSE_TAXONOMY.food as readonly string[] as string[];

export const FOOD_CONFIG: SubcategoryConfig = {
  keywords: FOOD_KEYWORDS,
  validSubcategories: VALID_FOOD_SUBCATEGORIES,
  minConfidenceThreshold: 0.3,
  subcategoryPriority: getFoodSubcategoryPriority(),
};

export function detectFoodSubcategory(description: string): string {
  return detectSubcategory(description, FOOD_CONFIG).subcategory;
}

export function detectFoodSubcategoryWithConfidence(
  description: string
): SubcategoryResult {
  return detectSubcategory(description, FOOD_CONFIG);
}

/**
 * Locale-aware food subcategory detector. See the equivalent comment
 * on `detectTransportSubcategoryWithLocale` — combines shared brand
 * keywords with the user's primary-language common-word keywords.
 * Returns the "default" sentinel when locale doesn't resolve so the
 * cloud LLM tier can disambiguate.
 *
 * The biggest win for food is shutting down cross-language collisions
 * like PT's "natural" (Brazilian sandwich type) polluting English
 * inputs ("natural gas" was previously matching food/sandwich because
 * 'natural' came from the merged map regardless of EN-user locale).
 */
export function detectFoodSubcategoryWithLocale(
  description: string,
  locale: string | undefined | null
): SubcategoryResult {
  const keywords = getLocaleAwareSubcategoryKeywords(
    b => b.foodSubcategories,
    k => k.foodSubcategories,
    locale
  );
  if (!keywords) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }
  return detectSubcategory(description, {
    ...FOOD_CONFIG,
    keywords,
  });
}
